package com.evoapps.evoshift.ui.courses

import com.evoapps.evoshift.widgets.SurfaceCard
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.draw.clip
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.Slideshow
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseKind
import com.evoapps.evoshift.domain.model.CoursePage
import com.evoapps.evoshift.domain.model.CoursePageKind
import com.evoapps.evoshift.domain.model.CourseRules
import com.evoapps.evoshift.domain.model.CoursePresentation
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.ui.richtext.RichTextView
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.documents.formatMoment
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.TakeCourseUiState
import com.evoapps.evoshift.viewmodels.TakeCourseViewModel
import com.evoapps.evoshift.widgets.EvoTopBar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.course_attempts_left
import evoshift.composeapp.generated.resources.course_back
import evoshift.composeapp.generated.resources.course_close
import evoshift.composeapp.generated.resources.course_draft_preview
import evoshift.composeapp.generated.resources.course_finish
import evoshift.composeapp.generated.resources.course_next
import evoshift.composeapp.generated.resources.course_no_attempts_today
import evoshift.composeapp.generated.resources.course_ohs_conducted_by
import evoshift.composeapp.generated.resources.course_ohs_consultation
import evoshift.composeapp.generated.resources.course_ohs_general_confirm
import evoshift.composeapp.generated.resources.course_ohs_hours
import evoshift.composeapp.generated.resources.course_ohs_station_note
import evoshift.composeapp.generated.resources.course_open_video
import evoshift.composeapp.generated.resources.course_page_of
import evoshift.composeapp.generated.resources.course_page_presentation
import evoshift.composeapp.generated.resources.course_presentation_file_note
import evoshift.composeapp.generated.resources.course_presentation_open
import evoshift.composeapp.generated.resources.course_presentation_open_file
import evoshift.composeapp.generated.resources.course_presentation_open_pdf
import evoshift.composeapp.generated.resources.course_presentation_privacy
import evoshift.composeapp.generated.resources.course_presentation_show
import evoshift.composeapp.generated.resources.course_passed_on
import evoshift.composeapp.generated.resources.course_pick_all
import evoshift.composeapp.generated.resources.course_pick_one
import evoshift.composeapp.generated.resources.course_result_failed
import evoshift.composeapp.generated.resources.course_result_passed
import evoshift.composeapp.generated.resources.course_result_score
import evoshift.composeapp.generated.resources.course_result_wrong
import evoshift.composeapp.generated.resources.course_retry
import evoshift.composeapp.generated.resources.course_send_answers
import evoshift.composeapp.generated.resources.course_start_quiz
import evoshift.composeapp.generated.resources.course_video_privacy
import evoshift.composeapp.generated.resources.course_quiz
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.jetbrains.compose.resources.stringResource

/**
 * SH-126: taking a course: its pages one by one (the person's progress is saved), then the quiz
 * (every question answered; the OHS general instruction also needs the employee's confirmation),
 * graded on the server, then the result with what was wrong and the attempts left today. Video
 * pages open outside the app, with a privacy note.
 */
@Composable
fun TakeCourseScreen(viewModel: TakeCourseViewModel, onNavigateBack: () -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = state.error?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    val course = state.course

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = course?.title ?: "", onBack = onNavigateBack) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(ContentMaxWidth)) {
            if (state.isLoading || course == null) {
                if (state.isLoading) CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (!course.isPublished) {
                    Text(stringResource(Res.string.course_draft_preview), color = TrainingOverdueColor, fontWeight = FontWeight.Bold)
                }
                state.completion?.takeIf { it.passed && !state.onResult }?.let { done ->
                    Text(
                        stringResource(Res.string.course_passed_on, done.completedAt?.let { formatMoment(it) }.orEmpty(), done.score),
                        color = TrainingDoneColor, fontWeight = FontWeight.Bold
                    )
                }
                if (course.kind.isOhs && state.step == 0) OhsHeader(course)
                when {
                    state.onResult -> ResultStep(state, onRetry = viewModel::retry, onClose = onNavigateBack)
                    state.onQuiz -> QuizStep(state, viewModel)
                    else -> course.pages.getOrNull(state.step)?.let { page ->
                        PageStep(state, page, viewModel)
                    }
                }
            }
        }
    }
}

@Composable
private fun OhsHeader(course: Course) {
    val ohs = course.ohs ?: return
    SurfaceCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(stringResource(course.kind.label()), fontWeight = FontWeight.Bold)
            Text(stringResource(Res.string.course_ohs_conducted_by, ohs.instructorName), style = MaterialTheme.typography.bodySmall)
            if (ohs.consultation.isNotBlank()) Text(stringResource(Res.string.course_ohs_consultation, ohs.consultation), style = MaterialTheme.typography.bodySmall)
            Text(stringResource(Res.string.course_ohs_hours, ohs.lessonHours), style = MaterialTheme.typography.bodySmall)
            if (course.kind == CourseKind.OHS_GENERAL) {
                Text(stringResource(Res.string.course_ohs_station_note), style = MaterialTheme.typography.bodySmall)
            }
        }
    }
}

@Composable
private fun PageStep(state: TakeCourseUiState, page: CoursePage, viewModel: TakeCourseViewModel) {
    val onBack = viewModel::back
    val onNext = viewModel::next
    val course = state.course ?: return
    Text(stringResource(Res.string.course_page_of, state.step + 1, state.pageCount), style = MaterialTheme.typography.labelMedium)
    LinearProgressIndicator(progress = { (state.step + 1).toFloat() / state.pageCount }, modifier = Modifier.fillMaxWidth())
    if (page.title.isNotBlank()) Text(page.title, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
    when (page.kind) {
        CoursePageKind.IMAGE -> {
            val bytes = page.imagePath?.let { state.images[it] }
            val bitmap = remember(bytes) { bytes?.let { runCatching { it.decodeToImageBitmap() }.getOrNull() } }
            if (bitmap != null) {
                Image(bitmap, contentDescription = page.title, contentScale = ContentScale.FillWidth, modifier = Modifier.fillMaxWidth().heightIn(max = 480.dp))
            } else if (page.imagePath != null) {
                CircularProgressIndicator()
            }
        }
        CoursePageKind.VIDEO_LINK -> {
            val uriHandler = LocalUriHandler.current
            page.url?.takeIf { CourseRules.isHttpsUrl(it) }?.let { url ->
                Button(onClick = { uriHandler.openUri(url) }) {
                    Icon(Icons.AutoMirrored.Filled.OpenInNew, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.course_open_video))
                }
            }
            Text(stringResource(Res.string.course_video_privacy), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        CoursePageKind.PRESENTATION -> page.presentation?.let { PresentationStep(page, it, state, viewModel) }
        CoursePageKind.TEXT -> Unit
    }
    // SH-211: a formatted lesson through the shared renderer; a plain one exactly as before.
    val rich = page.rich
    if (rich != null) {
        if (!rich.isBlank) RichTextView(rich)
    } else if (page.body.isNotBlank()) {
        Text(page.body, style = MaterialTheme.typography.bodyLarge)
    }
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        OutlinedButton(onClick = onBack, enabled = state.step > 0) { Text(stringResource(Res.string.course_back)) }
        val last = state.step == state.pageCount - 1
        Button(onClick = onNext) {
            Text(
                stringResource(
                    when {
                        !last -> Res.string.course_next
                        course.hasQuiz -> Res.string.course_start_quiz
                        else -> Res.string.course_finish
                    }
                )
            )
        }
    }
}

/**
 * SH-211: a presentation page. On the web the provider's player (or an uploaded PDF) is shown in
 * the page; elsewhere, and always as a second way, it opens outside: a link in the browser, an
 * uploaded file in the device's viewer ("Otwórz / Pobierz"). Showing or opening it counts the
 * page as seen.
 */
@Composable
private fun PresentationStep(page: CoursePage, presentation: CoursePresentation, state: TakeCourseUiState, viewModel: TakeCourseViewModel) {
    val uriHandler = LocalUriHandler.current
    val note = MaterialTheme.colorScheme.onSurfaceVariant
    val frameModifier = Modifier.fillMaxWidth().aspectRatio(16f / 10f).clip(RoundedCornerShape(12.dp))
    if (!presentation.isUpload) {
        val embed = presentation.embedUrl
        val link = presentation.link
        if (presentationsShowInline && embed != null) {
            // Loaded only when the person asks: the provider learns of the visit then (privacy policy).
            var showing by remember(page.id) { mutableStateOf(false) }
            if (showing) {
                PresentationFrame(embed, page.title.ifBlank { stringResource(Res.string.course_page_presentation) }, frameModifier)
                LaunchedEffect(page.id) { viewModel.presentationViewed() }
            } else {
                Button(onClick = { showing = true }) {
                    Icon(Icons.Default.Slideshow, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.course_presentation_show))
                }
            }
        }
        if (link != null && CourseRules.isHttpsUrl(link)) {
            OutlinedButton(onClick = { uriHandler.openUri(link); viewModel.presentationViewed() }) {
                Icon(Icons.AutoMirrored.Filled.OpenInNew, contentDescription = null)
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.course_presentation_open))
            }
        }
        Text(
            stringResource(Res.string.course_presentation_privacy, stringResource(presentation.source.label())),
            style = MaterialTheme.typography.bodySmall, color = note
        )
        return
    }
    val pdf = presentation.inlinePdfPath
    if (presentationsShowInline && pdf != null) {
        LaunchedEffect(pdf) { viewModel.loadFile(pdf) }
        val bytes = state.files[pdf]
        if (bytes != null) {
            PdfFrame(bytes, page.title.ifBlank { presentation.fileName.orEmpty() }, frameModifier)
            LaunchedEffect(page.id) { viewModel.presentationViewed() }
        } else {
            CircularProgressIndicator()
        }
    }
    val opening = state.openingFile != null
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
        Button(onClick = { viewModel.openPresentationFile() }, enabled = !opening) {
            Icon(Icons.Default.Download, contentDescription = null)
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.course_presentation_open_file))
        }
        if (presentation.pdfPath != null && presentation.fileType != DocumentFiles.PDF) {
            OutlinedButton(onClick = { viewModel.openPresentationFile(pdf = true) }, enabled = !opening) {
                Text(stringResource(Res.string.course_presentation_open_pdf))
            }
        }
        if (opening) CircularProgressIndicator(Modifier.width(20.dp), strokeWidth = 2.dp)
    }
    Text(
        stringResource(Res.string.course_presentation_file_note, presentation.fileName.orEmpty()),
        style = MaterialTheme.typography.bodySmall, color = note
    )
}

@Composable
private fun QuizStep(state: TakeCourseUiState, viewModel: TakeCourseViewModel) {
    val course = state.course ?: return
    Text(stringResource(Res.string.course_quiz), style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
    course.questions.forEachIndexed { index, question ->
        SurfaceCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text("${index + 1}. ${question.text}", fontWeight = FontWeight.Bold)
                Text(
                    stringResource(if (question.multi) Res.string.course_pick_all else Res.string.course_pick_one),
                    style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                val picked = state.answers[question.id].orEmpty()
                question.options.forEachIndexed { optionIndex, option ->
                    val selected = optionIndex in picked
                    val rowModifier = if (question.multi) {
                        Modifier.toggleable(value = selected, role = Role.Checkbox, onValueChange = { viewModel.toggleAnswer(question.id, optionIndex) })
                    } else {
                        Modifier.selectable(selected = selected, role = Role.RadioButton, onClick = { viewModel.toggleAnswer(question.id, optionIndex) })
                    }
                    Row(rowModifier.fillMaxWidth().padding(vertical = 2.dp), verticalAlignment = Alignment.CenterVertically) {
                        if (question.multi) Checkbox(checked = selected, onCheckedChange = null) else RadioButton(selected = selected, onClick = null)
                        Spacer(Modifier.width(8.dp))
                        Text(option)
                    }
                }
            }
        }
    }
    if (course.kind == CourseKind.OHS_GENERAL) {
        Row(
            Modifier.fillMaxWidth().toggleable(value = state.confirmed, role = Role.Checkbox, onValueChange = viewModel::setConfirmed),
            verticalAlignment = Alignment.Top
        ) {
            Checkbox(checked = state.confirmed, onCheckedChange = null)
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.course_ohs_general_confirm, course.ohs?.instructorName.orEmpty()))
        }
    }
    if (state.attemptsLeft <= 0) {
        Text(stringResource(Res.string.course_no_attempts_today), color = TrainingOverdueColor)
    } else {
        Text(stringResource(Res.string.course_attempts_left, state.attemptsLeft), style = MaterialTheme.typography.bodySmall)
    }
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        OutlinedButton(onClick = viewModel::back, enabled = course.pages.isNotEmpty()) { Text(stringResource(Res.string.course_back)) }
        Button(onClick = viewModel::submit, enabled = state.canSubmit) {
            if (state.isSubmitting) CircularProgressIndicator(Modifier.width(16.dp), strokeWidth = 2.dp)
            else Text(stringResource(if (course.hasQuiz) Res.string.course_send_answers else Res.string.course_finish))
        }
    }
}

@Composable
private fun ResultStep(state: TakeCourseUiState, onRetry: () -> Unit, onClose: () -> Unit) {
    val course = state.course ?: return
    val result = state.result
    val completion = state.completion ?: return
    Text(
        stringResource(if (completion.passed) Res.string.course_result_passed else Res.string.course_result_failed),
        style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold,
        color = if (completion.passed) TrainingDoneColor else TrainingOverdueColor
    )
    Text(stringResource(Res.string.course_result_score, completion.score, course.passPercent))
    val wrong = result?.wrongQuestionIds.orEmpty()
    if (!completion.passed && wrong.isNotEmpty()) {
        Text(stringResource(Res.string.course_result_wrong), fontWeight = FontWeight.Bold)
        course.questions.filter { it.id in wrong }.forEach { Text("• ${it.text}") }
    }
    if (!completion.passed) {
        if (state.attemptsLeft > 0) Text(stringResource(Res.string.course_attempts_left, state.attemptsLeft), style = MaterialTheme.typography.bodySmall)
        else Text(stringResource(Res.string.course_no_attempts_today), color = TrainingOverdueColor)
    }
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
        if (!completion.passed && state.attemptsLeft > 0) {
            OutlinedButton(onClick = onRetry) { Text(stringResource(Res.string.course_retry)) }
            Spacer(Modifier.width(8.dp))
        }
        Button(onClick = onClose) { Text(stringResource(Res.string.course_close)) }
    }
}
